'use client';
import { memo, useEffect, useMemo, useRef, useState } from 'react';

import { localeAtom } from '@core/Atoms/Locale/Locale.atom';
import { filtersListAtom } from '@core/Atoms/Plp/FiltersListAtom';
import { postcodeAtom } from '@core/Atoms/Postcode/Postcode.atom';
import { aggregationsAtom, initialAggregationsState } from '@core/Atoms/Search/AggregationsAtom';
import { AggregationsState } from '@core/Entities/Search/Aggregations/Aggregations.entity';
import { FilterKey, Filter as FilterType } from '@core/Entities/Search/SearchFilters/Filter.entity';
import { createPathFromFilters } from '@core/Utils/Search/CreatePathFromFilters';
import { getFilterFromArray } from '@core/Utils/SearchFilters/GetFilterFromArray';
import { mapFiltersForDisplay } from '@core/Utils/SearchFilters/MapFiltersForDisplay';
import { useLockBodyScroll } from '@core/Utils/UseBodyLockOnScroll';
import { Accordion } from '@gds/Accordion/Accordion';
import Button from '@gds/Button/Button';
import { Close } from '@gds/Icons/Paths/Close';
import { Filter } from '@gds/Icons/Paths/Filter';
import { Typography } from '@gds/Typography/Typography';
import { useIsExperimentEnabled } from '@growthBookExperimentation/Hooks/useIsExperimetEnabled';
import { useGetRoot } from '@search/Hooks/UseGetRoot';
import { useInitAggregations } from '@search/Hooks/UseInitAggregations';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useHydrateAtoms } from 'jotai/utils';
import { useRouter } from 'next/navigation';

import { dynamicFilterMessagesAtom } from 'Atoms/App/Plp/DynamicFilterMessagesAtom';
import { filterTrayOpenAtom } from 'Atoms/App/Plp/FilterTrayOpenAtom';
import { filterUpdateStartedAtom } from 'Atoms/App/Plp/FilterUpdateStarted';
import { priceTypeAtom } from 'Atoms/Search/PriceTypeAtom';

import { SavedSearchFilterCta } from '../SavedSearch/SavedSearchFilterCta';

import { ActiveFiltersOverview } from './Components/ActiveFiltersOverview/ActiveFiltersOverview';
import { CANT_DELETE } from './Components/ActiveFiltersOverview/ActiveFiltersOverview.constants';
import { BuyOnline } from './Components/BuyOnline/BuyOnline';
import { PromotionsFilter } from './Components/PromotionsFilter/PromotionsFilter';
import { FILTER_CATEGORIES } from './FilterPanel.constants';
import { FilterPanelProps, PlpFiltersMessagesProps } from './FilterPanel.entity';
import { trackFilterEvent } from './Filters.tracking';
import SearchAccordion from './SearchAccordion';

import styles from './FilterPanel.module.css';

export const FilterPanel = memo(
  ({
    locale,
    flags,
    strings,
    locationAttrs,
    dealerAttrs,
    tenant,
    initialAggregations,
    initialActiveFilters,
    enableFilterAnchors = true,
  }: FilterPanelProps) => {
    useInitialValues({ initialAggregations, initialActiveFilters });

    const root = useGetRoot();
    const [filtersList, setFiltersList] = useAtom(filtersListAtom);
    const dynamicStrings = useAtomValue(dynamicFilterMessagesAtom);
    useInitAggregations(locale);
    const router = useRouter();
    const setLocale = useSetAtom(localeAtom);
    const savedPostcode = useAtomValue(postcodeAtom);
    const postcodeFilter = getFilterFromArray('postcode', filtersList);
    const canIncludeSoldCarFilter = useIsExperimentEnabled('unavailable-cars-leadform-experiment', [
      '1',
      '2',
    ]);

    const filterUpdateStarted = useAtomValue(filterUpdateStartedAtom);
    const [isFiltersOpen, setIsFiltersOpen] = useAtom(filterTrayOpenAtom);
    const [isShowingAllFilters, setIsShowingAllFilters] = useState(false);
    const setPriceType = useSetAtom(priceTypeAtom);

    useLockBodyScroll(isFiltersOpen);

    const filterCategoriesIntl = FILTER_CATEGORIES.map(type => {
      const filterValue = getFilterFromArray(type.id as FilterKey, filtersList);
      const labelAddon = Array.isArray(filterValue?.value) ? ` (${filterValue?.value.length})` : '';
      const isDisabled =
        !!(type?.parentId && !getFilterFromArray(type.parentId as FilterKey, filtersList)) ||
        (locale === 'uk' && type.id !== 'location' && !savedPostcode[locale] && !postcodeFilter);

      const isHidden =
        (type.id === 'variant-type' && !getFilterFromArray('make', filtersList)) ||
        (type.id === 'includeSold' && !canIncludeSoldCarFilter) ||
        (type.getIsHidden &&
          type.getIsHidden({
            locale,
            tenant,
            flags,
          }));

      return {
        ...type,
        isHidden,
        isDisabled,
        label: strings[type.id as keyof PlpFiltersMessagesProps] + labelAddon,
      };
    });

    useEffect(() => {
      setLocale(locale);
    }, [locale]);

    useEffect(() => {
      if (filtersList.some(item => item.id.includes('monthly'))) {
        setPriceType('monthly-price');
      } else {
        setPriceType('price');
      }
    }, []);

    const clearAll = () => {
      const cantDeleteFilters: FilterType[] = filtersList.filter(fltr =>
        CANT_DELETE[locale]?.includes(fltr.id),
      );

      setFiltersList(cantDeleteFilters);
      const newUrl = createPathFromFilters(cantDeleteFilters, locale, root);
      router.push(newUrl);
    };
    const combinedStrings = { ...strings, ...dynamicStrings };
    const canClearAll = filtersList.filter(fltr => !CANT_DELETE[locale]?.includes(fltr.id)).length;

    const areaFilter = filtersList.find(filter => filter.id === 'area');
    const regionFilter = filtersList.find(filter => filter.id === 'region');
    const dealershipFilter = filtersList.find(filter => filter.id === 'dealership-id');
    const promotionFilter = filtersList.find(filter => filter.id === 'has-offers-or-promotions');

    let filtersLength = mapFiltersForDisplay(filtersList, locale).length;

    if (areaFilter) {
      filtersLength -= 3;
    } else if (regionFilter || dealershipFilter) {
      filtersLength -= 2;
    }

    return (
      <aside
        aria-label={strings.panelLabel}
        className={`${styles.filtersCol} ${isFiltersOpen ? styles.isOpen : ''}`}
        onClick={() => setIsFiltersOpen(false)}
      >
        <div
          className={`${styles.filtersWrapper} ${
            isShowingAllFilters ? styles.isShowingAllFilters : ''
          }`}
          onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}
        >
          <div className={styles.filtersHeader}>
            <Typography Component="p" variant="h5">
              {strings.heading}
              {filtersLength > 0 && ` (${filtersLength})`}
            </Typography>

            <button
              aria-label={strings.closeBtn}
              className={styles.closeBtn}
              data-test-id="close-filter-panel-btn"
              onClick={() => setIsFiltersOpen(false)}
            >
              <Close />
            </button>
            <Button
              className={styles.clearAllBtn}
              disabled={!canClearAll}
              data-test-id="clear-all-filters-btn"
              onClick={clearAll}
              variant="link"
            >
              {strings.clearAllBtn}
            </Button>
          </div>
          <div className={styles.filtersContent}>
            <ActiveFiltersOverview
              locale={locale}
              strings={combinedStrings}
              noBottomBorder={flags.savedSearch}
              locationAttrs={locationAttrs}
              dealerAttrs={dealerAttrs}
            />
            {flags.savedSearch && (
              <SavedSearchFilterCta
                locale={locale}
                strings={strings}
                locationAttrs={locationAttrs}
                dealerAttrs={dealerAttrs}
              />
            )}

            {filterCategoriesIntl
              .filter(
                category => !category.restrictLocales || category.restrictLocales.includes(locale),
              )
              .map(category => {
                const { Component, id, isDisabled, isHidden, isMainFilter } = category;
                const AccordionComponent =
                  id === 'make' || id === 'model' || id === 'variant_type'
                    ? SearchAccordion
                    : Accordion;

                if (isHidden) return null;
                if (!flags.buyOnlineFilterTop && id === 'buyOnlineExp') {
                  return null;
                }
                if (flags.buyOnlineFilterTop && id === 'buyOnlineExp') {
                  return (
                    <BuyOnline key="buy-online" strings={combinedStrings} isDisabled={isDisabled} />
                  );
                }
                if (flags.promotionsFilter && id === 'promotions_exclusives') {
                  return (
                    <PromotionsFilter
                      key="promotions"
                      strings={combinedStrings}
                      isDisabled={isDisabled}
                      promotionFilter={promotionFilter}
                    />
                  );
                }
                if (!flags.promotionsFilter && id === 'promotions_exclusives') {
                  return null;
                }
                return (
                  <AccordionComponent
                    key={id}
                    className={`${styles.filterAccord} ${
                      !isMainFilter ? styles.secondaryFilter : ''
                    }`}
                    title={category.label || id}
                    open={!isDisabled ? category.defaultOpen : false}
                    disabled={isDisabled}
                    dataTestId={`${id}-filter`}
                    onExpandedChange={expanded =>
                      trackFilterEvent('features', expanded ? 'open' : 'close', filtersList)
                    }
                    renderWhenClosed
                  >
                    <Component
                      locale={locale}
                      strings={combinedStrings}
                      tenant={tenant}
                      isPlpFiltersSeoEnabled={enableFilterAnchors}
                    />
                  </AccordionComponent>
                );
              })}
          </div>
          <div className={styles.mobileCtas}>
            <Button className={styles.clearBtnMob} onClick={clearAll} variant="outlined">
              {strings.clearBtn}
            </Button>

            <Button
              className={styles.searchBtnMob}
              loading={filterUpdateStarted}
              onClick={() => setIsFiltersOpen(false)}
            >
              {dynamicStrings.showResultsBtn}
            </Button>
          </div>
        </div>

        <Button
          className={styles.viewMoreBtn}
          dataTestId="view-all-filters-toggle"
          leftIcon={<Filter />}
          onClick={() => setIsShowingAllFilters(!isShowingAllFilters)}
          variant="outlined"
        >
          {isShowingAllFilters ? strings.viewLessFilters : strings.viewMoreFilters}
        </Button>
      </aside>
    );
  },
);

FilterPanel.displayName = 'FilterPanel';

/**
 * A combination of `dangerouslyForceHydrate` and a manual tracking of whether it's been hydrated
 * via a ref and a `useEffect` is to ensure that new server-side values get hydrated, but only once
 * on the client-side.
 *
 * Otherwise, new SSR values don't get hydrated and the UI shows stale data even after a new session.
 */
const useInitialValues = ({
  initialAggregations,
  initialActiveFilters,
}: Pick<FilterPanelProps, 'initialActiveFilters' | 'initialAggregations'>) => {
  const hydrated = useRef(false);
  const aggregations = useMemo(
    (): AggregationsState =>
      initialAggregations
        ? { ...initialAggregations, isLoading: false, error: false }
        : initialAggregationsState,
    [initialAggregations],
  );

  useHydrateAtoms(
    // only hydrate the first time - on client side
    !hydrated.current
      ? [
          [filtersListAtom, initialActiveFilters ?? []],
          [aggregationsAtom, aggregations],
        ]
      : [],
    {
      dangerouslyForceHydrate: true,
    },
  );

  useEffect(() => {
    hydrated.current = true;
  }, []);
};
